// src/pages/services/purchase/poNumberService.js
//
// PO NUMBER — ONE SERIES PER COMPANY, PER YEAR (P2b, MED-21)
//
// WHY: the counter used to be ONE global doc (system_counters/po_{year})
// shared by every company, so a new client's first PO continued another
// client's series (and showed how many POs that client had made). Each
// company now has its own counter: system_counters/po_{company_id}_{year}.
//
// FORMAT (unchanged): PO-2026-0001
//
// CONTINUITY: the first time a company's counter doc for a year does not
// exist, its series starts after the highest PO number this company
// already has for that year (read once, before the transaction). An
// existing client continues its own numbering; a new client starts at
// 0001 — no manual seeding. The old global po_{year} docs are history
// only; firestore.rules refuses writes to them, so a browser tab still
// running old code fails loudly instead of handing out a clashing number.
//
// A refused or failed save can leave a gap in numbers — never a duplicate.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../../../firebase";

// Highest NNNN among this company's existing "PO-<year>-NNNN" numbers (0 if none).
const highestExistingPONumber = async (company_id, year) => {
  const prefix = `PO-${year}-`;
  const snap = await getDocs(query(
    collection(db, "purchase_orders"),
    where("company_id", "==", company_id)
  ));
  let max = 0;
  snap.docs.forEach((d) => {
    const no = String(d.data().po_number || "");
    if (!no.startsWith(prefix)) return;
    const n = parseInt(no.slice(prefix.length), 10);
    if (Number.isFinite(n) && n > max) max = n;
  });
  return max;
};

export const generatePONumber = async (company_id) => {
  const companyId = String(company_id || "").trim();
  if (!companyId) throw new Error("company_id is required to create a PO number");

  const year       = new Date().getFullYear();
  const counterRef = doc(db, "system_counters", `po_${companyId}_${year}`);

  // The seed is needed only once per company per year. It is read outside
  // the transaction (a query cannot run inside one); the transaction uses
  // it only if the counter doc still does not exist. If this read fails,
  // the PO is NOT created — numbering never silently restarts at 0001.
  const existing = await getDoc(counterRef);
  const seed = existing.exists() ? 0 : await highestExistingPONumber(companyId, year);

  const nextNumber = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const last = snap.exists() ? Number(snap.data().last_number || 0) : seed;
    const next = last + 1;
    transaction.set(counterRef, {
      counter_type: "purchase_order",
      company_id:   companyId,
      year,
      last_number:  next,
      updated_at:   serverTimestamp(),
    }, { merge: true });
    return next;
  });

  return `PO-${year}-${String(nextNumber).padStart(4, "0")}`;
};